/**
 *  @description functionality to work with the GlobalMercator information.
 *  Mainbly used by the orthotile to be able to calculate tiles and perform geographic interpolations.
 */

// Proj4 Text:
// +proj=merc + a=6378137 + b=6378137 + lat_ts=0.0 + lon_0=0.0 + x_0=0.0 + y_0=0
//  + k=1.0 + units=m + nadgrids= @null +no_defs

export class GlobalMercator {
  private tileSize: number;
  private initialResolution: number;
  private originShift: number;

  constructor(tileSize: number) {
    this.tileSize = tileSize;
    // 156543.03392804062 for tileSize 256 PIxels
    this.initialResolution = (2 * Math.PI * 6378137) / this.tileSize;
    // 20037508.342789244
    this.originShift = (2 * Math.PI * 6378137) / 2.0;
  }

  /**
   * @description Converts given lat/lon in WGS84 Datum to XY in Spherical Mercator EPSG:900913
   */
  latLongToMeters(lat: number, lng: number) {
    const x = (lng * this.originShift) / 180.0;
    let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360.0)) / (Math.PI / 180.0);

    y = (y * this.originShift) / 180.0;
    return { x, y };
  }

  /**
   * @description Converts XY point from Spherical Mercator EPSG:900913 to lat/lon in WGS84 Datum
   */
  metersToLatLon(x: number, y: number) {
    const lon = (x / this.originShift) * 180.0;
    let lat = (y / this.originShift) * 180.0;

    lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180.0)) - Math.PI / 2.0);
    return { lat, lon };
  }

  /**
   * @description Converts PIxel coordinates in given zoom level of pyramid to EPSG:900913
   */
  pixelsToMeters(px: number, py: number, zoom: number) {
    const res = this.resolution(zoom);
    const mx = px * res - this.originShift;
    const my = py * res - this.originShift;
    return { mx, my };
  }

  /**
   * @description Converts EPSG:900913 to pyramid PIxel coordinates in given zoom level
   */
  metersToPixels(mx: number, my: number, zoom: number) {
    const res = this.resolution(zoom);
    const px = (mx + this.originShift) / res;
    const py = (my + this.originShift) / res;
    return { px, py };
  }

  /**
   * @description Returns a tile covering region in given PIxel coordinates
   */
  pixelsToTile(px: number, py: number) {
    const tx = Math.ceil(px / this.tileSize) - 1;
    const ty = Math.ceil(py / this.tileSize) - 1;
    return { tx, ty };
  }

  /**
   * @description Move the origin of PIxel coordinates to top-left corner
   */
  pixelsToRaster(px: number, py: number, zoom: number) {
    const mapSize = this.tileSize << zoom;
    return { px, py: mapSize - py };
  }

  /**
   * @description Returns tile for given mercator coordinates
   */
  metersToTile(mx: number, my: number, zoom: number) {
    const pixel = this.metersToPixels(mx, my, zoom);
    return this.pixelsToTile(pixel.px, pixel.py);
  }

  /**
   * @description Returns tile for given mercator coordinates
   */
  latLongToTile(lng: number, lat: number, zoom: number, google = true) {
    const meters = this.latLongToMeters(lat, lng);
    const pixel = this.metersToPixels(meters.x, meters.y, zoom);
    const tileIndex = this.pixelsToTile(pixel.px, pixel.py);
    if (google) {
      tileIndex.ty = this.googleTile(tileIndex.tx, tileIndex.ty, zoom).ty;
    }

    return tileIndex;
  }

  /**
   * @description Returns tile for given mercator coordinates
   *
   * @param {Number} zoom
   */
  tileToMeterWidth(zoom: number, google = true) {
    return this.resolution(zoom) * this.tileSize;
  }

  /**
   * @description Returns bounds of the given tile in EPSG:900913 coordinates
   */
  tileBounds(tx: number, ty: number, zoom: number, google = true) {
    if (google) {
      ty = this.googleTile(tx, ty, zoom).ty;
    }

    const minBound = this.pixelsToMeters(tx * this.tileSize, ty * this.tileSize, zoom);
    const maxBound = this.pixelsToMeters((tx + 1) * this.tileSize, (ty + 1) * this.tileSize, zoom);
    return {
      minx: minBound.mx,
      miny: minBound.my,
      maxx: maxBound.mx,
      maxy: maxBound.my,
    };
  }

  /**
   * @description Returns bounds of the given tile in latutude/longitude using WGS84 datum
   */
  tileLatLonBounds(tx: number, ty: number, zoom: number, google = true) {
    if (google) {
      ty = this.googleTile(tx, ty, zoom).ty;
    }

    const bounds = this.tileBounds(tx, ty, zoom, false);
    const minBounds = this.metersToLatLon(bounds.minx, bounds.miny);
    const maxBounds = this.metersToLatLon(bounds.maxx, bounds.maxy);

    return {
      south: minBounds.lat,
      west: minBounds.lon,
      north: maxBounds.lat,
      east: maxBounds.lon,
    };
  }

  /**
   * @description Resolution (meters/PIxel) for given zoom level (measured at Equator)
   */
  resolution(zoom: number) {
    return this.initialResolution / Math.pow(2, zoom);
  }

  /**
   * @description Converts TMS tile coordinates to Google Tile coordinates
   */
  googleTile(tx: number, ty: number, zoom: number) {
    // Coordinate origin is moved from bottom-left to top-left corner of the extent
    return { tx, ty: Math.pow(2, zoom) - 1 - ty };
  }
}
